import { createContext, ReactNode, useContext } from "react";
import { FieldErrors } from "react-hook-form";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";
import ConfirmationModal from "components/ConfirmationModal";
import { showErrorMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";
import { IEditFundingRequest } from "../../EditFundingRequest/StepOne";
import { useFundingRequestForm } from "./FundingRequestFormContext";
import {
  canBrokerEditApprovedFactoredFR,
  canBrokerEditApprovedSelfFinanceOffPlatformFR,
  canBrokerEditApprovedSelfFinanceCarrierFields,
  canBrokerEditApprovedSelfFinanceShipperAmount,
  canBrokerEditSelfFinanceInvoiceReferenceFields,
  isCarrierFRFieldsLocked
} from "./permissions";
import SelfFinanceEditConfirmContent from "./SelfFinanceEditConfirmContent";

interface FundingRequestUpdateActionContextType {
  isAssignedLoadsMode: boolean;
  isCompletionStateActive: boolean;
  isButtonDisabled: () => boolean;
  updateBtnLabel: () => ReactNode;
  handleSubmitClick: (e: React.FormEvent) => void;
}

const FundingRequestUpdateActionContext =
  createContext<FundingRequestUpdateActionContextType | null>(null);

// HP-9361: the Update / Mark as Delivered button is rendered by RightActions (desktop) and by the
// mobile swiper's sticky bars, and on mobile both are mounted at once. One provider keeps a single
// copy of the rules and a single set of confirmation modals for every button.
export function FundingRequestUpdateActionProvider({ children }: { children: ReactNode }) {
  const {
    isSubmitting,
    sendInvoice,
    isGlobalDisabled,
    selectedFR,
    submitFR,
    refetch,
    methods,
    hasBOLDocument,
    isAssignedLoadsPage,
    isMarkedComplete,
    isTypeChanged,
    isOtherFieldsChanged,
    isCarrierFieldsChanged,
    isSelfFinanceOffPlatformEditPending
  } = useFundingRequestForm();
  const [isSelfFinanceEditConfirmOpen, toggleSelfFinanceEditConfirmModal] = useToggle();
  const [isResendInvoiceConfirmOpen, toggleResendInvoiceConfirmModal] = useToggle();
  const { isCarrier, isBroker } = useUserFeature();

  const contractType = methods.watch("fundingRequestType");
  const isNonFactoredFR = [
    FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD,
    FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT
  ].includes(contractType?.value ?? "");

  const isCarrierCompletionMode =
    isCarrier &&
    contractType?.value === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT &&
    selectedFR?.contractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT &&
    !isTypeChanged &&
    !isOtherFieldsChanged;
  const isAssignedLoadsMode = isAssignedLoadsPage || isCarrierCompletionMode;
  const isCompletionStateActive =
    isAssignedLoadsMode &&
    isMarkedComplete &&
    !["Incomplete", "Document Issue"].includes(selectedFR?.status ?? "");

  const handleFormSubmit = async (): Promise<boolean> => {
    let didSave = false;
    await methods.handleSubmit(
      async (data) => {
        try {
          // Call the submitFR function from context with the form data
          didSave = await submitFR(data);
          if (didSave) {
            // Refresh the form data after successful submission
            await refetch();
          }
        } catch (error) {
          didSave = false;
          if (error !== "cancel-edit-fr") {
            showErrorMessage("Failed to submit the form. Please try again.");
          }
        }
      },
      (_errors: FieldErrors<IEditFundingRequest>) => {
        showErrorMessage("Please fix the form errors before submitting.");
      }
    )();
    return didSave;
  };

  // HP-9120: read once and share with isButtonDisabled below. The same permission already unlocks
  // these inputs in CarrierInformation.tsx, so keeping the button gate and the confirmation gate on
  // one value is what stops them disagreeing again.
  const canEditSelfFinanceCarrierFields = canBrokerEditApprovedSelfFinanceCarrierFields({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    carrierEditLocked: selectedFR?.carrierEditLocked,
    carrierFundingCommitted: selectedFR?.carrierFundingCommitted
  });

  // HP-8882: a carrier-side edit on an approved self-finance FR recomputes carrier amounts and can
  // reset the payout clock, so confirm the recomputed amounts before saving.
  // HP-9100: suppressed when the off-platform edit confirmation (FundingRequestFormContext) will
  // fire instead — otherwise both confirmations show in sequence for the same edit.
  const isSelfFinanceCarrierEdit =
    canEditSelfFinanceCarrierFields &&
    isCarrierFieldsChanged &&
    !isSelfFinanceOffPlatformEditPending;

  // HP-8865: editing the shipper invoice amount on an approved self-finance-with-invoice FR
  // recomputes the debtor invoice (HaulPay's AR) and offers an invoice resend on confirm.
  // HP-9100: suppressed for the off-platform case for the same reason as above — that FR type's
  // resend prompt is handled once, from inside the off-platform edit confirmation instead.
  const isSelfFinanceShipperEdit =
    canBrokerEditApprovedSelfFinanceShipperAmount({
      isBroker,
      status: selectedFR?.status,
      contractType: selectedFR?.contractType,
      receivableStatus: selectedFR?.receivableStatus
    }) &&
    !!methods.formState.dirtyFields?.shipperInvoiceAmount &&
    !isSelfFinanceOffPlatformEditPending;

  const needsSelfFinanceEditConfirm = isSelfFinanceCarrierEdit || isSelfFinanceShipperEdit;

  // Wrapper for the form submission to handle the event
  const handleSubmitClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (needsSelfFinanceEditConfirm) {
      toggleSelfFinanceEditConfirmModal();
      return;
    }
    void handleFormSubmit();
  };

  // Update button label based on form state and assigned loads logic (V1 parity)
  const updateBtnLabel = (): ReactNode => {
    if (isSubmitting) return <Icon path={mdiLoading} size={1} spin={1} />;

    if (isAssignedLoadsMode) {
      if (isCompletionStateActive) {
        return `Marked as ${isAssignedLoadsPage ? "Delivered" : "completed"}`;
      }
      return `Mark as ${isAssignedLoadsPage ? "Delivered" : "completed"}`;
    }
    return "Update";
  };

  const isButtonDisabled = (): boolean => {
    // An in-flight save disables the button regardless of which permission carve-out applies. The
    // carve-outs below return early, so checking this last left them able to submit again before
    // the first update resolved -- on an off-platform edit that means a second rate recalculation,
    // a second invoice version, and two resend prompts.
    if (isSubmitting) return true;

    if (isAssignedLoadsMode) {
      if (
        (!hasBOLDocument && isAssignedLoadsMode) ||
        (isMarkedComplete &&
          selectedFR?.status &&
          !["Incomplete", "Document Issue"].includes(selectedFR.status))
      ) {
        return true;
      }
      if (selectedFR?.status && !["Incomplete", "Document Issue"].includes(selectedFR.status))
        return true;
    }

    if (
      canBrokerEditApprovedFactoredFR({
        isBroker,
        status: selectedFR?.status,
        debtorName: selectedFR?.debtor?.name ?? null,
        isNonFactoredFR
      })
    ) {
      return false;
    }

    const offPlatformEdit = canBrokerEditApprovedSelfFinanceOffPlatformFR({
      isBroker,
      status: selectedFR?.status,
      contractType: selectedFR?.contractType,
      carrierEditLocked: selectedFR?.carrierEditLocked,
      carrierFundingCommitted: selectedFR?.carrierFundingCommitted,
      receivableStatus: selectedFR?.receivableStatus
    });
    if (offPlatformEdit.canEditCarrierFields || offPlatformEdit.canEditShipperAmount) {
      return false;
    }

    // HP-9120: the on-platform self-finance types never got a branch here. The factored one above
    // bails on the "No Debtor" sentinel a SELF_FINANCE_NO_INVOICE FR carries, and the off-platform
    // one covers its own contract type only, so an approved No Invoice FR fell through to the
    // global lock: its carrier inputs were editable but the edit could never be submitted.
    if (canEditSelfFinanceCarrierFields) {
      return false;
    }

    // HP-9058: PO # and Client Invoice # stay editable in one state the two checks above do not
    // cover — an approved or paid off-platform FR with no receivable row. Without this the fields
    // render enabled and Update stays greyed out.
    if (
      canBrokerEditSelfFinanceInvoiceReferenceFields({
        isBroker,
        status: selectedFR?.status,
        contractType: selectedFR?.contractType,
        receivableStatus: selectedFR?.receivableStatus
      })
    ) {
      return false;
    }

    if (isGlobalDisabled || isCarrierFRFieldsLocked({ isCarrier, status: selectedFR?.status }))
      return true;
    return false;
  };

  return (
    <FundingRequestUpdateActionContext.Provider
      value={{
        isAssignedLoadsMode,
        isCompletionStateActive,
        isButtonDisabled,
        updateBtnLabel,
        handleSubmitClick
      }}>
      {children}

      <ConfirmationModal
        title="Confirm changes"
        toggleConfirmModal={toggleSelfFinanceEditConfirmModal}
        isConfirmModalOpen={isSelfFinanceEditConfirmOpen}
        content={<SelfFinanceEditConfirmContent />}
        labelOKButton="Save changes"
        isLoading={isSubmitting}
        onOK={async () => {
          const offerResend = isSelfFinanceShipperEdit;
          const didSave = await handleFormSubmit();
          if (!didSave) return;
          toggleSelfFinanceEditConfirmModal();
          if (offerResend) {
            toggleResendInvoiceConfirmModal();
          }
        }}
      />

      <ConfirmationModal
        title="Resend invoice?"
        toggleConfirmModal={toggleResendInvoiceConfirmModal}
        isConfirmModalOpen={isResendInvoiceConfirmOpen}
        content="You changed the shipper invoice amount. Do you want to resend the updated invoice to the debtor now?"
        labelOKButton="Resend invoice"
        labelCancelButton="Not now"
        onOK={async () => {
          try {
            await sendInvoice();
          } finally {
            toggleResendInvoiceConfirmModal();
          }
        }}
      />
    </FundingRequestUpdateActionContext.Provider>
  );
}

export function useFundingRequestUpdateAction() {
  const ctx = useContext(FundingRequestUpdateActionContext);
  if (!ctx)
    throw new Error(
      "useFundingRequestUpdateAction must be used within a FundingRequestUpdateActionProvider"
    );
  return ctx;
}
